import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Send, Calendar, ChevronDown, ChevronUp, MessageSquare, Copy, Check, AlertCircle, Loader2 } from 'lucide-react';
import { InlineWidget } from 'react-calendly';

export const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [expandedSection, setExpandedSection] = useState<'form' | 'calendly' | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    setIsMobile(typeof window !== 'undefined' && 'ontouchstart' in window);
  }, []);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // ignore
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://haulkmqkgpdkfizncnoy.supabase.co';
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhhdWxrbXFrZ3Bka2Zpem5jbm95Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTE5NzIxNzEsImV4cCI6MjEwNzU0ODE3MX0.WPfkhLf3eWv72z_VeHc02WLSYa8VMl5eG1p3HngNzzU';

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || `Błąd serwera (${response.status})`);
      }

      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err.message || 'Nie udało się wysłać wiadomości. Spróbuj ponownie.');
      setTimeout(() => setStatus('idle'), 8000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleSection = (section: 'form' | 'calendly') => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const contactInfo = {
    email: 'biuro@marketengine.pl',
    phone: '507 456 292'
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-blue-600/20 to-slate-900/20">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-4 text-gradient text-glow">{t('contact.title')}</h2>
        <p className="text-blue-200/80 text-center mb-12 max-w-2xl mx-auto">{t('contact.subtitle')}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 max-w-4xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex items-center justify-between group hover:bg-white/10 transition-all duration-300">
            <div className="flex items-center flex-1 min-w-0">
              <div className="bg-blue-500/20 p-3 rounded-lg mr-4 flex-shrink-0">
                <Mail className="w-6 h-6 text-blue-300" />
              </div>
              <div className="min-w-0">
                <h3 className="text-white font-semibold">{t('contact.email')}</h3>
                <p className="text-blue-200/80 truncate">{contactInfo.email}</p>
              </div>
            </div>
            <button
              onClick={() => handleCopy(contactInfo.email, 'email')}
              className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors ml-4"
              title="Kopiuj email"
            >
              {copiedField === 'email' ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5 text-blue-300" />
              )}
            </button>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 flex items-center justify-between group hover:bg-white/10 transition-all duration-300">
            <div className="flex items-center flex-1 min-w-0">
              <div className="bg-blue-500/20 p-3 rounded-lg mr-4 flex-shrink-0">
                <Phone className="w-6 h-6 text-blue-300" />
              </div>
              <div className="min-w-0">
                <h3 className="text-white font-semibold">{t('contact.phone')}</h3>
                <p className="text-blue-200/80 truncate">{contactInfo.phone}</p>
              </div>
            </div>
            <button
              onClick={() => handleCopy(contactInfo.phone, 'phone')}
              className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors ml-4"
              title="Kopiuj telefon"
            >
              {copiedField === 'phone' ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5 text-blue-300" />
              )}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
            <button
              onClick={() => toggleSection('form')}
              className="w-full p-6 flex justify-between items-center border-b border-white/10"
            >
              <div className="flex items-center space-x-3">
                <MessageSquare className="w-6 h-6 text-blue-300" />
                <h3 className="text-xl font-semibold text-white">{t('contact.form.submit')}</h3>
              </div>
              <div className="md:hidden">
                {expandedSection === 'form' ? (
                  <ChevronUp className="w-5 h-5 text-blue-300" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-blue-300" />
                )}
              </div>
            </button>

            <div className={`transition-all duration-300 ${expandedSection === 'form' || !isMobile ? 'block' : 'hidden'}`}>
              <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-blue-200/80 mb-2" htmlFor="name">
                      {t('contact.form.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      minLength={2}
                      className="w-full bg-white/5 border border-blue-500/30 rounded-lg px-4 py-3 text-blue-100 focus:outline-none focus:border-blue-500/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-blue-200/80 mb-2" htmlFor="email">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-white/5 border border-blue-500/30 rounded-lg px-4 py-3 text-blue-100 focus:outline-none focus:border-blue-500/60 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-blue-200/80 mb-2" htmlFor="message">
                      {t('contact.form.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      minLength={5}
                      rows={6}
                      className="w-full bg-white/5 border border-blue-500/30 rounded-lg px-4 py-3 text-blue-100 focus:outline-none focus:border-blue-500/60 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-500 transition-all flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Wysyłanie...</span>
                      </>
                    ) : (
                      <>
                        <span>{t('contact.form.submit')}</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  {status === 'success' && (
                    <div className="flex items-center space-x-2 text-green-400 bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                      <Check className="w-5 h-5 flex-shrink-0" />
                      <p>{t('contact.success')}</p>
                    </div>
                  )}

                  {status === 'error' && (
                    <div className="flex items-start space-x-2 text-red-400 bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <p>{errorMsg || t('contact.error')}</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
            <button
              onClick={() => toggleSection('calendly')}
              className="w-full p-6 flex justify-between items-center border-b border-white/10"
            >
              <div className="flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-blue-300" />
                <h3 className="text-xl font-semibold text-white">{t('contact.calendly.title')}</h3>
              </div>
              <div className="md:hidden">
                {expandedSection === 'calendly' ? (
                  <ChevronUp className="w-5 h-5 text-blue-300" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-blue-300" />
                )}
              </div>
            </button>

            <div className={`transition-all duration-300 ${expandedSection === 'calendly' || !isMobile ? 'block' : 'hidden'}`}>
              <InlineWidget
                url="https://calendly.com/mierzejewski111/spotkanie"
                styles={{ height: '600px' }}
                pageSettings={{
                  backgroundColor: 'ffffff',
                  hideEventTypeDetails: false,
                  hideLandingPageDetails: false,
                  primaryColor: '3b82f6',
                  textColor: 'ffffff'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
